<script lang="ts" setup>
import {computed, ref, onMounted, watch} from 'vue';
import {getRandomId} from '@/ts/utils/pure-functions';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useCustomAnswerFuzzyMatching, type RatedCustomAnswer} from '@/ts/composables/pure/use-custom-answer-suggestions';
import type {QuestionnaireCustomAnswer} from '@/ts/types/dto/interventions-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';

const props = defineProps<{
  questionId: UuidDTO;
  query: string;
}>();

const emit = defineEmits<{
  select: [answer: string];
}>();

const api = useApi();
const customAnswers = ref<QuestionnaireCustomAnswer[]>([]);
const isLoading = ref<boolean>(false);
const activeIndex = ref<number>(-1);
const containerRef = ref<HTMLElement>();

// Load custom answers on mount
onMounted(async() => {
  try {
    isLoading.value = true;
    const response = await api.me.getQuestionnaireCustomAnswers();
    customAnswers.value = response.customAnswers;
  } catch (_error) {
    // Error loading custom answers - component will work without suggestions
  } finally {
    isLoading.value = false;
  }
});

// Filter answers for current question and remove duplicates
const relevantAnswers = computed(() => {
  const questionAnswers = customAnswers.value
    .filter(answer => answer.questionId === props.questionId)
    .map(answer => answer.customAnswer?.trim() ?? '')
    .filter(answer => (answer?.length ?? 0) > 0);
  
  // Remove duplicates
  return [...new Set(questionAnswers)];
});

// Use the shared fuzzy matching composable
const filteredAndRankedAnswers = useCustomAnswerFuzzyMatching(
  relevantAnswers,
  computed(() => props.query),
);


const isListExpanded = computed<boolean>(() => {
  return filteredAndRankedAnswers.value.length > 0 && !isLoading.value;
});

const onSelect = (answer: RatedCustomAnswer): void => {
  emit('select', answer.customAnswer);
};

// Keyboard navigation functions
const focusIndex = (index: number): void => {
  if (index >= 0 && index < filteredAndRankedAnswers.value.length) {
    activeIndex.value = index;
  }
};

const selectActiveItem = (): void => {
  if (activeIndex.value >= 0 && activeIndex.value < filteredAndRankedAnswers.value.length) {
    const activeAnswer = filteredAndRankedAnswers.value[activeIndex.value];
    onSelect(activeAnswer);
  }
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (!isListExpanded.value) {return;}
  
  switch (event.key) {
    case 'ArrowUp':
      event.preventDefault();
      focusIndex(activeIndex.value - 1);
      break;
    case 'ArrowDown':
      event.preventDefault();
      focusIndex(activeIndex.value + 1);
      break;
    case 'Enter':
      event.preventDefault();
      selectActiveItem();
      break;
    case 'Escape':
      event.preventDefault();
      activeIndex.value = -1;
      break;
    case 'Home':
      event.preventDefault();
      focusIndex(0);
      break;
    case 'End':
      event.preventDefault();
      focusIndex(filteredAndRankedAnswers.value.length - 1);
      break;
    default:
      // No action needed for other keys
      break;
  }
};

// Reset active index when suggestions change
watch([filteredAndRankedAnswers, isListExpanded], () => {
  if (!isListExpanded.value) {
    activeIndex.value = -1;
  } else if (filteredAndRankedAnswers.value.length > 0 && activeIndex.value >= filteredAndRankedAnswers.value.length) {
    activeIndex.value = 0;
  }
});

// Generate unique IDs for ARIA
const suggestionListId = `suggestions-list-${getRandomId()}`;
const optionIdPrefix = `suggestion-option-${getRandomId()}-`;

// Expose methods for parent component
defineExpose({
  focusIndex,
  containerRef,
});

</script>

<template>
  <div
    ref="containerRef"
    class="suggestions-container"
    :class="{'suggestions-container--visible': isListExpanded}"
    tabindex="-1"
    @keydown="handleKeydown"
  >
    <div 
      v-if="isListExpanded" 
      :id="suggestionListId"
      class="suggestions-pills"
      role="listbox"
      :aria-label="$t('custom_answer_suggestions')"
    >
      <div
        v-for="(answer, index) in filteredAndRankedAnswers"
        :id="optionIdPrefix + index"
        :key="index"
        class="suggestion-pill"
        :class="{
          'suggestion-pill--selected': props.query.trim() === answer.customAnswer,
          'suggestion-pill--active': activeIndex === index
        }"
        role="option"
        :aria-selected="props.query.trim() === answer.customAnswer"
        :tabindex="activeIndex === index ? 0 : -1"
        @click="onSelect(answer)"
        @mouseenter="activeIndex = index"
        @mouseleave="activeIndex = -1"
      >
        {{ answer.customAnswer }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.suggestions-container {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.2s ease, padding 0.3s ease;
  background: var(--Neutral-UI-neutral-ui-0);
  border-top: 1px solid var(--Neutral-UI-neutral-ui-30);
  border-radius: 0 0 var(--Border-Radius-3) var(--Border-Radius-3);
  padding: 0 var(--Spacing-spacing-3);
  width: 100%;
  
  &--visible {
    max-height: none;
    opacity: 1;
    padding: var(--Spacing-spacing-3);
  }
}

.suggestions-pills {
  display: flex;
  flex-wrap: wrap;
  gap: var(--Spacing-spacing-2);
}

.suggestion-pill {
  display: inline-block;
  padding: var(--Spacing-spacing-2) var(--Spacing-spacing-3);
  background: var(--Neutral-UI-neutral-ui-10);
  color: var(--Neutral-UI-neutral-ui-80);
  border: 1px solid var(--Neutral-UI-neutral-ui-30);
  border-radius: 20px;
  cursor: pointer;
  @include font-body-bold;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  line-height: 1.4;
  word-wrap: break-word;
  
  &:hover {
    background: var(--Primary-primary-10);
    color: var(--Primary-primary-60);
    border-color: var(--Primary-primary-30);
    transform: translateY(-1px);
  }
  
  &--selected {
    background-color: var(--Neutral-UI-neutral-ui-80);
    color: var(--Neutral-UI-neutral-ui-10);
    border-color: var(--Neutral-UI-neutral-ui-80);
    
    &:hover {
      background-color: var(--Neutral-UI-neutral-ui-80);
      color: var(--Neutral-UI-neutral-ui-10);
      border-color: var(--Neutral-UI-neutral-ui-80);
      transform: translateY(-1px);
    }
  }
  
  &--active {
    background: var(--Primary-primary-10);
    color: var(--Primary-primary-60);
    border-color: var(--Primary-primary-30);
    outline: 2px solid var(--Primary-primary-40);
    outline-offset: 1px;
    
    &:focus {
      outline: 2px solid var(--Primary-primary-40);
      outline-offset: 1px;
    }
  }
}
</style>